import jsonrpc from '@polkadot/types/interfaces/jsonrpc'
import {
  isWeb3Injected,
  web3Accounts,
  web3Enable,
  web3EnablePromise,
  web3FromSource,
  web3ListRpcProviders,
} from '@polkadot/extension-dapp'
import { ApiPromise, WsProvider } from '@polkadot/api'
import { keyring as Keyring } from '@polkadot/ui-keyring'
import { isTestChain } from '@polkadot/util'

import { toast } from 'react-toastify'

import { setItem } from '../services/localStorage/localStorage.service'
import { API_STATE, ASSETID } from 'contexts/wallet.context'
import { toDecimal, transformParams } from 'utils'
import { getInstanceByAccount } from './callmethods/dct'
import { getAssetBalance } from './callmethods/assets'
import { getSGiantBalance, getStakes } from './callmethods/staking'
import { getBalance, retrieveChainInfo } from './callmethods/system'

// type HANDLE_WALLET_CONNECT = () => void
type GET_ACCOUNTS = (provider: string) => Promise<any[]>

// const wsProvider = new WsProvider('wss://rpc.polkadot.io')
const wsProvider = new WsProvider(process.env.REACT_APP_WSPROVIDER?.split(','))
export const apiProvider: any = new ApiPromise({ provider: wsProvider, rpc: jsonrpc })

export const getAccounts: GET_ACCOUNTS = async (provider) => {
  const accounts = await web3Accounts()

  let providerAccounts = accounts.filter((account: any) => account.meta.source === provider)

  let allAccounts = accounts.map(({ address, meta }) => ({
    address,
    meta: { ...meta, name: `${meta.name} (${meta.source})` },
  }))
  const { systemChain, systemChainType } = await retrieveChainInfo()

  const isDevelopment = systemChainType.isDevelopment || systemChainType.isLocal || isTestChain(systemChain)
  const formattedAccounts = accounts.map(account => ({
    ...account,
    meta: {
      ...account.meta,
      genesisHash: account.meta.genesisHash?.startsWith('0x')
        ? account.meta.genesisHash as `0x${string}`
        : null,
    },
  }));
  Keyring.loadAll({ isDevelopment }, formattedAccounts)
  return providerAccounts
}

export function toUint8Array(json: any): number[] {
  const str = JSON.stringify(json)

  var bytes = new Uint8Array(str.length)

  for (var iii = 0; iii < str.length; iii++) {
    bytes[iii] = str.charCodeAt(iii)
  }

  return Array.from(new Uint8Array(bytes))
}

const getFromAcct = async (currentAccount: any) => {
  const {
    address,
    meta: { source },
  } = currentAccount

  // if (!isInjected) {
  //   return [currentAccount]
  // }

  // currentAccount is injected from polkadot-JS extension, need to return the addr and signer object.
  // ref: https://polkadot.js.org/docs/extension/cookbook#sign-and-send-a-transaction
  const injectedWeb3 = (window as any).injectedWeb3
  const ext = await injectedWeb3[source].enable()

  const injector = await ext.signer
  return [address, { signer: injector }]
}

export const handleEvents = (dispatch: any) => {
  dispatch({ type: 'SET_API_STATE', payload: API_STATE.CONNECT_INIT })
  apiProvider.on('connected', () => {
    apiProvider.isReady.then(async (_api: any) => {
      // let meta = await getMetadata()
      // dispatch({ type: 'SET_METADATA', payload: meta })
      dispatch({ type: 'SET_API', payload: _api })
      dispatch({ type: 'SET_API_STATE', payload: API_STATE.READY })
    })
  })
  apiProvider.on('ready', () => {
    dispatch({ type: 'SET_API_STATE', payload: API_STATE.READY })
  })
  apiProvider.on('error', () => {
    // dispatch({ type: 'SET_API_STATE', payload: API_STATE.ERROR })
    dispatch({ type: 'SET_CONNECT_WALLET_LOADER', payload: false })
  })
}

export const txSend = async (
  accounts: any[],
  paramFields: Boolean[],
  inputParams: any,
  palletRPC: string,
  callable: string,
) => {
  const fromAcct = await getFromAcct(accounts)
  let params = transformParams(paramFields, inputParams)

  const txExecute = apiProvider.tx[palletRPC][callable](...params)
  // const unsub = txExecute.signAndSend(...fromAcct, txResHandler).catch(txErrHandler)
  return { txExecute, fromAcct }
  // console.log(unsub)
}

export const fetchStakeBalance = async (account: any) => {
  try {
    let stakes = await getStakes(account.address)
    let deciStakes = {
      total: toDecimal(stakes.total).deci,
      earnedBalance: toDecimal(stakes.reward).deci,
    }
    return deciStakes
  } catch (e: any) {
    return {
      total: '0',
      earnedBalance: '0',
    }
  }
}

export const handleWalletConnect = async (account: any, decimal: any) => {
  let walletDetails = {
    account: '',
    bal: 0,
    isConnected: false,
  }
  try {
    setItem<boolean>('isConnected', true)
    let balance = await getBalance(account)
    let assetBalance = await getAssetBalance(account)
    let sGiantBalance = await getSGiantBalance(account)
    return {
      bal: balance,
      sGiantBalance: sGiantBalance,
      assetBal: assetBalance,
      isConnected: true,
      account: account,
    }
  } catch (e) {
    // console.log('wallet-connection-error', e)
    return walletDetails
  }
}

export const getPolkadotAccounts = async (provider: string) => {
  const injectedWeb3 = (window as any).injectedWeb3
  let walletDetails = {
    account: '',
  }
  if (injectedWeb3[provider]) {
    try {
      // console.log(provider)
      const ext = await injectedWeb3[provider].enable()
      if (!ext.accounts.subscribe) {
        ext.accounts.subscribe = (cb: any) => {
          ext.accounts
            .get()
            .then((cb: any) => console.log('ACCOUNTS', cb))
            .catch(console.error)
          return () => {
            // console.log('unsubscribe', cb)
            // no ubsubscribe needed, this is a single-shot
          }
        }
      }
      // console.log(wallets[0].accounts, web3EnablePromise)
      // let accounts = await getAccounts(provider)
      let accounts = await ext.accounts.get()

      let accs = accounts.map((account: any) => {
        return {
          address: account.address,
          meta: {
            name: account.name,
            source: provider,
          },
        }
      })

      return {
        account: accs,
      }
    } catch (e) {
      // console.log('accounts-error', e)
      return walletDetails
    }
  } else {
    toast.warn(`Please install ${provider} extension!`, {
      toastId: 'wallet-connection-error',
    })
    throw new Error("Extension doesn't exist")
  }
}

export const getExtrinsicFailed = (events: any, setTxnLoading?: any) => {
  events
    // find/filter for failed events
    .filter(({ event }: any) => apiProvider.events.system.ExtrinsicFailed.is(event))
    // we know that data for system.ExtrinsicFailed is
    // (DispatchError, DispatchInfo)
    .forEach(
      ({
        event: {
          data: [error, info],
        },
      }: any) => {
        if (error.isModule) {
          // for module errors, we have the section indexed, lookup
          const decoded = apiProvider.registry.findMetaError(error.asModule)
          const { docs, method, section } = decoded

          toast.error(`${section}.${method}: ${docs.join(' ')}`)
          setTxnLoading(false)
        } else {
          // Other, CannotLookup, BadOrigin, no extra info
          toast.error('Invalid transaction')
          setTxnLoading(false)
          return
        }
      },
    )
}
